/**
 * @file Builds the investment schema, adding the profile-type input, the
 * matching profile sub-schema and the tiered investments input.
 */
import { W1 } from "@components/Constants";
import {
  TYPE_NONE,
  TYPE_ARRAY,
  TYPE_RADIO,
  TYPE_HIDDEN,
} from "@components/Form";
import { MarkdownAlert } from "@components/Text";
import {
  INVESTMENT_PROFILE_TYPE_JOINT,
  INVESTMENT_PROFILE_TYPE_SDIRA,
  INVESTMENT_PROFILE_TYPE_ENTITY,
  INVESTMENT_PROFILE_TYPE_INDIVIDUAL,
} from "@components/Domain";
import type { SchemaInput } from "@api";
import { updateMyInvestmentOperation } from "@api/services/investments";

import InvestmentsInput from "../InvestmentsInput";
import getProfileJointSchema from "./getProfileJointSchema";
import getProfileSdiraSchema from "./getProfileSdiraSchema";
import getProfileEntitySchema from "./getProfileEntitySchema";
import renderProfileTypeOptions from "./renderProfileTypeOptions";
import getProfileIndividualSchema from "./getProfileIndividualSchema";
import type {
  Tier,
  Input,
  Investment,
  ProfilesMap,
  Organization,
  UpdateSchema,
  GetInvestorSsnNumber,
} from "../types";

const LABEL_PROFILE_TYPE = "How do you like to invest?";

const WARNING_STYLE = { marginTop: W1, marginBottom: 0 };
const INVESTMENTS_ITEM_STYLE = { marginBottom: 0 };

// The positions of the profile inputs and the investments input in the
// schema; extendBlockedInputs relies on the last one.
const JOINT_INPUT_INDEX = 1;
const SDIRA_INPUT_INDEX = 2;
const ENTITY_INPUT_INDEX = 3;
const INDIVIDUAL_INPUT_INDEX = 4;
const INVESTMENTS_INPUT_INDEX = 5;

type Options = {
  tiers: Tier[];
  sponsorId?: string;
  investment?: Investment;
  profilesMap: ProfilesMap;
  organization: Organization;
  updateSchema: UpdateSchema;
  otherInvestments?: Investment[];
  investorAccountId?: string;
  getInvestorSsnNumber?: GetInvestorSsnNumber;
  isTypeSelectDisabled: boolean;
  profileTypeExtraWarning: string | null;
  profileNameExtraWarning: string | null;
};

/**
 * Builds the investment schema for the current profile type, plugging in
 * the matching profile sub-schema and hiding the investments input until
 * a profile type is chosen.
 */
const getInvestmentSchema = ({
  investment = {},
  otherInvestments = [],
  getInvestorSsnNumber = () => undefined,
  tiers,
  sponsorId,
  profilesMap,
  organization,
  updateSchema,
  investorAccountId,
  isTypeSelectDisabled,
  profileTypeExtraWarning,
  profileNameExtraWarning,
}: Options): SchemaInput[] => {
  const isIndividualDisabled = otherInvestments
    .filter(({ profileType }) => profileType === INVESTMENT_PROFILE_TYPE_INDIVIDUAL)
    .length > 0;

  const inputs: Input[] = [
    {
      name: "profileType",
      type: TYPE_RADIO,
      label: LABEL_PROFILE_TYPE,
      options: renderProfileTypeOptions(isIndividualDisabled),
      inputProps: {
        disabled: isTypeSelectDisabled,
        /** Refreshes the schema after the profile type changes. */
        onChange: () => {
          updateSchema();
        },
      },
      itemProps: {
        extra: (
          profileTypeExtraWarning
            ? (
              <MarkdownAlert
                text={profileTypeExtraWarning}
                type="warning"
                style={WARNING_STYLE}
              />
            )
            : null
        ),
      },
    },
    {
      name: "profileJoint",
      type: TYPE_NONE,
    },
    {
      name: "profileSdira",
      type: TYPE_NONE,
    },
    {
      name: "profileEntity",
      type: TYPE_NONE,
    },
    {
      name: "profileIndividual",
      type: TYPE_NONE,
    },
    {
      name: "investments",
      type: TYPE_ARRAY,
      component: InvestmentsInput,
      componentProps: {
        tiers,
        itemProps: { style: INVESTMENTS_ITEM_STYLE },
      },
    },
    {
      name: "receiverSourceId",
      type: TYPE_NONE,
    },
    {
      name: "receiverSourceName",
      type: TYPE_NONE,
    },
    {
      name: "isProfileValid",
      type: TYPE_NONE,
    },
    {
      name: "isImmutable",
      type: TYPE_NONE,
    },
  ];

  if (sponsorId) {
    inputs.push({
      name: "sponsorId",
      type: TYPE_HIDDEN,
      initialValue: sponsorId,
    });
  }

  if (investorAccountId) {
    inputs.push({
      name: "investorAccountId",
      type: TYPE_HIDDEN,
      initialValue: investorAccountId,
    });
  }

  const { profileType } = investment;

  const isJoint = profileType === INVESTMENT_PROFILE_TYPE_JOINT;
  const isSdira = profileType === INVESTMENT_PROFILE_TYPE_SDIRA;
  const isEntity = profileType === INVESTMENT_PROFILE_TYPE_ENTITY;
  const isIndividual = profileType === INVESTMENT_PROFILE_TYPE_INDIVIDUAL;

  if (isJoint) {
    const { id, profileJoint = {}, investor } = investment;
    const existingJointProfiles = profilesMap[INVESTMENT_PROFILE_TYPE_JOINT];
    const isEdit = !!id;

    const jointInput = inputs[JOINT_INPUT_INDEX];

    jointInput.label = "";
    jointInput.schema = getProfileJointSchema(organization, updateSchema, getInvestorSsnNumber, isEdit, profileJoint, investor, existingJointProfiles);
    delete jointInput.type;
  }

  if (isSdira) {
    const { profileSdira = {}, id } = investment;
    const existingSdiraProfiles = profilesMap[INVESTMENT_PROFILE_TYPE_SDIRA];
    const isEdit = !!id;

    const sdiraInput = inputs[SDIRA_INPUT_INDEX];

    sdiraInput.label = "";
    sdiraInput.schema = getProfileSdiraSchema(organization, updateSchema, isEdit, profileNameExtraWarning, profileSdira, existingSdiraProfiles);
    delete sdiraInput.type;
  }

  if (isEntity) {
    const { id, profileEntity = {} } = investment;
    const existingEntityProfiles = profilesMap[INVESTMENT_PROFILE_TYPE_ENTITY];
    const isEdit = !!id;

    const entityInput = inputs[ENTITY_INPUT_INDEX];

    entityInput.label = "";
    entityInput.schema = getProfileEntitySchema(organization, updateSchema, isEdit, profileNameExtraWarning, profileEntity, existingEntityProfiles);
    delete entityInput.type;
  }

  if (isIndividual) {
    let { profileIndividual = {} } = investment;
    const existingIndividualProfiles = profilesMap[INVESTMENT_PROFILE_TYPE_INDIVIDUAL];
    const hasProfileIndividual = Object.values(profileIndividual).length > 0;
    const hasExistingIndividualProfiles = existingIndividualProfiles.length > 0;

    if (!hasProfileIndividual && hasExistingIndividualProfiles) {
      const [ recentProfile ] = existingIndividualProfiles;
      profileIndividual = recentProfile.profileIndividual;
    }

    const individualInput = inputs[INDIVIDUAL_INPUT_INDEX];

    individualInput.label = "";
    individualInput.schema = getProfileIndividualSchema(organization, updateSchema, getInvestorSsnNumber, profileIndividual);

    delete individualInput.type;
  }

  if (!profileType) {
    inputs[INVESTMENTS_INPUT_INDEX].type = TYPE_NONE;
  }

  const schema = updateMyInvestmentOperation.getSchema(inputs);

  return schema;
};

export default getInvestmentSchema;
